import React, { memo, useCallback } from "react";
import { Box, Link, Paper, Tooltip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { VscFiles } from "react-icons/vsc";
import { BiGitBranch } from "react-icons/bi";
import { MdOutlineLightMode, MdOutlineDarkMode } from "react-icons/md";
import Divider from "@mui/material/Divider";
import { links } from "../pages/links";
import { useColorMode } from "../theme/theme";

interface Props {
  expanded: boolean;
  setExpanded: React.Dispatch<React.SetStateAction<boolean>>;
}

function Sidebar({
  expanded,
  setExpanded,
}: Props) {
  const theme = useTheme();
  const v = theme.palette.vscode;
  const { mode, toggle } = useColorMode();

  const handleToggleExpanded = useCallback(() => {
    setExpanded((prev) => !prev);
  }, [setExpanded]);

  return (
    <Box
      sx={{
        height: "100%",
        backgroundColor: v.activityBar,
      }}
      justifyContent="space-between"
      display="flex"
      flexDirection="column"
      component={Paper}
      square
      elevation={0}
    >
      <Box
        sx={{ flexGrow: 0 }}
        display="flex"
        justifyContent="center"
        flexDirection="column"
      >
        <Box
          sx={{
            borderLeft: `solid 0.12em ${
              expanded ? v.activityBorderActive : v.activityBorderInactive
            }`,
            cursor: "pointer",
            WebkitTapHighlightColor: "rgba(0,0,0,0)",
          }}
          onClick={handleToggleExpanded}
        >
          <Box
            sx={{
              flexGrow: 0,
              my: 1.5,
              color: expanded ? v.activityIconHover : v.activityIcon,
              fontSize: 24,
              outline: "none",
              "&:hover": {
                color: v.activityIconHover,
              },
            }}
            display="flex"
            justifyContent="center"
          >
            <VscFiles />
          </Box>
        </Box>
        <Tooltip title="Source of this project" arrow placement="right">
          <Link
            target="_blank"
            href={"https://github.com/john7rho/react-vscode-portfolio"}
            underline="none"
            color="inherit"
            sx={{ WebkitTapHighlightColor: "rgba(0,0,0,0)" }}
          >
            <Box
              sx={{
                flexGrow: 0,
                cursor: "pointer",
                color: v.activityIcon,
                fontSize: 24,
                "&:hover": {
                  color: v.activityIconHover,
                },
              }}
              display="flex"
              justifyContent="center"
            >
              <Box mt={0.7}>
                <BiGitBranch />
              </Box>
            </Box>
          </Link>
        </Tooltip>

        <Divider sx={{ m: 0.5 }} />

        {links.map((link) => (
          <Tooltip title={link.title} arrow placement="right" key={link.index}>
            <Link
              target="_blank"
              href={link.href}
              underline="none"
              color="inherit"
              sx={{ WebkitTapHighlightColor: "rgba(0,0,0,0)" }}
            >
              <Box
                sx={{
                  flexGrow: 0,
                  m: 0.5,
                  color: v.activityIcon,
                  fontSize: 24,
                  "&:hover": {
                    color: v.activityIconHover,
                  },
                  cursor: "pointer",
                }}
                display="flex"
                justifyContent="center"
              >
                <Box mt={0.7}>{link.icon}</Box>
              </Box>
            </Link>
          </Tooltip>
        ))}
      </Box>

      <Box
        sx={{ flexGrow: 0, mb: 1 }}
        display="flex"
        justifyContent="center"
        flexDirection="column"
      >
        <Tooltip
          title={mode === "dark" ? "Switch to light theme" : "Switch to dark theme"}
          arrow
          placement="right"
        >
          <Box
            role="button"
            aria-label="Toggle light/dark theme"
            onClick={toggle}
            sx={{
              flexGrow: 0,
              m: 0.5,
              color: v.activityIcon,
              fontSize: 24,
              "&:hover": {
                color: v.activityIconHover,
              },
              cursor: "pointer",
              WebkitTapHighlightColor: "rgba(0,0,0,0)",
            }}
            display="flex"
            justifyContent="center"
          >
            <Box mt={0.7} display="flex">
              {mode === "dark" ? <MdOutlineLightMode /> : <MdOutlineDarkMode />}
            </Box>
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );
}

export default memo(Sidebar);
